/* import __COLOCATED_TEMPLATE__ from './shopify.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import { restartableTask } from 'ember-concurrency-decorators';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isValidEmail } from 'embercom/lib/email';
import { isValidOrderName } from 'embercom/lib/inbox/shopify/order';
import { taskFor } from 'ember-concurrency-ts';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import { type TaskGenerator, timeout } from 'ember-concurrency';
import type User from 'embercom/objects/inbox/user';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import ConversationAttributeSummary from 'embercom/objects/inbox/conversation-attribute-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import { TrackedArray, TrackedSet } from 'tracked-built-ins';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type { Customer, Order, ShippingAddress, IntegrationResponse } from './shopify/types';

interface Args {
  user?: User;
  conversation: Conversation;
  conversationAttributeDescriptors?: ConversationAttributeDescriptor[];
  isOpen: boolean;
  onOpenSectionChange: () => void;
  onExpandedSectionChange: () => void;
  isExpanded: boolean;
  name: string;
  conversationId: number;
  userId: string;
}

interface Signature {
  Args: Args;
}

interface ShopifyCustomerDataError {
  key:
    | 'no_store_selected'
    | 'embercom_shopify_store_not_connected'
    | 'embercom_shopify_user_email_not_in_shopify'
    | 'invalid_email_or_order_name'
    | 'embercom_shopify_unauthorized'
    | 'no_store_connected'
    | 'unknown';
  message?: string;
}

interface ShopifyCustomerDataResponse {
  customer: Customer;
  customer_orders: Order[];
  all_customer_orders_url: string;
}

export default class Shopify extends Component<Signature> {
  @service declare session: Session;
  @service declare permissionsService: any;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare intl: IntlService;

  @tracked shopifyCustomer?: Customer;
  @tracked shopifyCustomerOrdersUrl?: string;
  @tracked orders = new TrackedArray<Order>([]);

  @tracked shopifyCustomerDataError: ShopifyCustomerDataError | undefined;

  @tracked previousUserId: string | null | undefined;
  @tracked previousConversationId: number | null | undefined;
  @tracked previousConversationShopifyDomain: string | undefined;
  @tracked previousUserShopifyDomain: string | undefined;

  // PII: Don't use masked email as Shopify search term
  @tracked shopifyEmailOrOrderName?: string | null | undefined = this.args.user?.emailMasked
    ? undefined
    : this.args.user?.email;
  @tracked shopifyDomain: string | undefined;

  @tracked selectedTab = 'customer';
  @tracked showCreateOrderModal = false;
  @tracked showAllOrders = false;
  @tracked expandCustomer = false;
  @tracked isOrderSearch = false;
  @tracked expandedOrderIds = new TrackedSet<number>();
  @tracked allConnectedStores: IntegrationResponse[] = [];
  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.fetchShopifyDataTask).perform();
  }

  get storeName() {
    return typeof this.shopifyDomain === 'string' ? this.shopifyDomain.split('.')[0] : undefined;
  }

  get hasOrders() {
    return this.orders && this.orders.length > 0;
  }

  get userShopifyDomain() {
    return this.args.user?.attributes?.custom_data?.shopify_domain as string | undefined;
  }

  get conversationShopifyDomain() {
    return this.args.conversation?.attributes?.find(
      (attr) => attr.descriptor.name === 'shopify_domain',
    )?.value as string | undefined;
  }

  get defaultShopifyDomain(): string | undefined {
    // we prioritize conversation shopify_domain value
    // as that's exactly where conversation started
    return this.conversationShopifyDomain || this.userShopifyDomain || undefined;
  }

  get emailIsValid() {
    return isValidEmail(this.shopifyEmailOrOrderName);
  }

  get canManageIntegrations(): boolean {
    return this.permissionsService.currentAdminCan('can_manage_apps_and_integrations');
  }

  get connectToMoreStoresFooter() {
    if (!this.canManageIntegrations) {
      return null;
    }

    return {
      text: this.intl.t('inbox.conversation-details-sidebar.shopify.connect-more-stores'),
      icon: 'reference',
      action: this.connectToMoreStores,
    };
  }

  get dropDownOptions() {
    return this.allConnectedStores.map((shop) => {
      let isHealthy = shop.installation_status === 'OK';
      return {
        value: shop.shopify_domain,
        text: isHealthy
          ? shop.shopify_domain
          : this.intl.t('inbox.conversation-details-sidebar.shopify.store-unhealthy', {
              storeName: shop.shopify_domain,
            }),
      };
    });
  }

  get hasUnhealthyStores(): boolean {
    return this.allConnectedStores.some((shop) => shop.installation_status !== 'OK');
  }

  get shopifySettingsUrl(): string {
    return `/a/apps/${this.session.workspace.id}/settings/app-settings/app-store?app_package_code=shopify`;
  }

  get isReadOnly() {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-read-only');
  }

  get newestOrder(): Order | undefined {
    if (!this.isOrderSearch) {
      return this.orders?.[0];
    }

    let searchTerm = this.shopifyEmailOrOrderName?.trim();
    let formattedTerm = searchTerm?.startsWith('#') ? searchTerm : `#${searchTerm}`;
    return this.orders?.find((order) => order.name === formattedTerm) || this.orders?.[0];
  }

  get shopifyCustomerDataErrorKey() {
    return this.shopifyCustomerDataError?.key;
  }

  get shopifyCustomerDataErrorMessage() {
    return this.shopifyCustomerDataError?.message;
  }

  @restartableTask
  *debouncedFetch(): TaskGenerator<void> {
    yield timeout(300);
    yield taskFor(this.fetchShopifyDataTask).perform();
  }

  @restartableTask
  *fetchShopifyDataTask(shop_domain?: string): TaskGenerator<void> {
    this.shopifyCustomer = undefined;
    this.shopifyCustomerDataError = undefined;
    this.showAllOrders = false;
    this.expandCustomer = false;
    this.expandedOrderIds.clear();

    yield taskFor(this.loadShopifyDomains).perform();

    if (this.allConnectedStores.length === 0) {
      this.shopifyCustomerDataError = { key: 'no_store_connected' };
      return;
    }

    let connectedStoreDomains = this.allConnectedStores.map((store) => store.shopify_domain);
    if (connectedStoreDomains.length === 1) {
      // always select the single store connected
      this.shopifyDomain = connectedStoreDomains[0];
    } else if (shop_domain && connectedStoreDomains.includes(shop_domain)) {
      // if provided shopify_domain valid, use it
      this.shopifyDomain = shop_domain;
    } else if (
      this.defaultShopifyDomain &&
      connectedStoreDomains.includes(this.defaultShopifyDomain)
    ) {
      // if defaultShopifyDomain - from user or conversation attribute value
      this.shopifyDomain = this.defaultShopifyDomain;
    } else {
      this.shopifyDomain = undefined;
      this.shopifyCustomerDataError = { key: 'no_store_selected' };
      return;
    }

    let data: {
      app_id: string;
      shopify_domain: string;
      user_email?: string;
      order_name?: string;
    } = {
      app_id: this.session.workspace.id,
      shopify_domain: this.shopifyDomain,
    };

    if (this.shopifyEmailOrOrderName && isValidEmail(this.shopifyEmailOrOrderName)) {
      data['user_email'] = this.shopifyEmailOrOrderName;
      this.isOrderSearch = false;
    } else if (this.shopifyEmailOrOrderName && isValidOrderName(this.shopifyEmailOrOrderName)) {
      data['order_name'] = this.shopifyEmailOrOrderName;
      this.isOrderSearch = true;
    } else {
      this.shopifyCustomerDataError = { key: 'invalid_email_or_order_name' };
      return;
    }

    try {
      let shopifyResponse = yield ajax({
        url: '/ember/inbox/shopify/shopify_customer_data',
        type: 'GET',
        data,
      }) as ShopifyCustomerDataResponse;

      this.shopifyCustomer = shopifyResponse.customer;
      this.orders = new TrackedArray(shopifyResponse.customer_orders);
      this.shopifyCustomerOrdersUrl = shopifyResponse.all_customer_orders_url;
    } catch (error) {
      captureException(error);
      if (error.jqXHR?.responseJSON?.error) {
        this.shopifyCustomerDataError = error.jqXHR.responseJSON.error;
      } else {
        this.shopifyCustomerDataError = { key: 'unknown' };
      }
    }
  }

  @restartableTask
  *loadShopifyDomains(): TaskGenerator<void> {
    try {
      let response = yield ajax({
        url: '/ember/shopify/integrations',
        type: 'GET',
        data: {
          app_id: this.session.workspace.id,
        },
      }) as IntegrationResponse[];

      this.allConnectedStores = response;
    } catch (error) {
      captureException(error);
    }
  }

  @action
  connectToMoreStores() {
    safeWindowOpen(this.shopifySettingsUrl, '_blank');
    taskFor(this.fetchShopifyDataTask).perform();
  }

  @action
  intercomUserChanged(user: User | undefined, conversation: Conversation | undefined) {
    // PII: Don't use masked email as Shopify search term
    this.shopifyEmailOrOrderName = this.args.user?.emailMasked ? undefined : this.args.user?.email;
    let conversationShopifyDomainAttribute = conversation?.attributes?.find(
      (attr) => attr.descriptor.name === 'shopify_domain',
    )?.value as string | undefined;
    let userShopifyDomainAttribute = user?.attributes?.custom_data?.shopify_domain as
      | string
      | undefined;

    if (
      this.previousUserId !== user?.id ||
      this.previousConversationId !== conversation?.id ||
      this.previousConversationShopifyDomain !== conversationShopifyDomainAttribute ||
      this.previousUserShopifyDomain !== userShopifyDomainAttribute
    ) {
      this.previousUserId = user?.id;
      this.previousConversationId = conversation?.id;
      this.previousConversationShopifyDomain = conversationShopifyDomainAttribute;
      this.previousUserShopifyDomain = userShopifyDomainAttribute;
      taskFor(this.debouncedFetch).perform();
    }
  }

  @action
  focusOnEmailSearchInput() {
    let emailOrOrderSearchInput = document.querySelector<HTMLElement>(
      '.js__shopify-email-or-order-search-input',
    );
    emailOrOrderSearchInput && emailOrOrderSearchInput.focus();
  }

  @action
  async shopSelectedChanged(value: string) {
    if (value === '__connect_more_stores__') {
      safeWindowOpen(this.shopifySettingsUrl, '_blank');
      taskFor(this.fetchShopifyDataTask).perform();
      return;
    }
    await this.updateConversationAttribute(value);
    taskFor(this.fetchShopifyDataTask).perform(value);
  }

  // Updates the conversation's shopify_domain attribute
  // when user CDA: 1. is not set or 2. when it does not point to the right shop
  // Option 2 would happen when user places order on 2 differnt stores
  // but we always return the most recent user (which might not have the right shopify domain)
  async updateConversationAttribute(value: string) {
    let descriptor = this.args.conversationAttributeDescriptors?.find(
      (descriptor) => descriptor.name === 'shopify_domain',
    );

    if (descriptor) {
      let attribute = new ConversationAttributeSummary(value, descriptor);

      await this.inboxApi.updateAttribute(this.args.conversation.id, attribute);
    }
  }

  @action
  toggleExpandCustomer() {
    this.expandCustomer = !this.expandCustomer;
  }

  @action
  toggleOrderExpanded(orderId: number) {
    if (this.expandedOrderIds.has(orderId)) {
      this.expandedOrderIds.delete(orderId);
    } else {
      this.expandedOrderIds.add(orderId);
    }
  }

  @action
  navigateToOrder(fromOrderId: number, toOrderId: number) {
    this.showAllOrders = true;
    this.expandedOrderIds.delete(fromOrderId);
    this.expandedOrderIds.add(toOrderId);
  }

  @action
  setShowAllOrders() {
    this.showAllOrders = true;
  }

  @action
  backButtonClicked() {
    if (this.showAllOrders) {
      this.showAllOrders = false;
    } else {
      this.expandCustomer = false;
    }
    this.expandedOrderIds.clear();
  }

  @action
  updateExpandedOrderShippingAddress(orderId: number, newShippingAddress: ShippingAddress) {
    let index = this.orders.findIndex((o) => o.id !== null && Number(o.id) === Number(orderId));
    if (index !== -1) {
      this.orders[index] = { ...this.orders[index], shipping_address: newShippingAddress };
    }
  }

  @action
  handleRefundSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }

  @action
  handleReplacementSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }

  @action
  handleGiftCardSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify': typeof Shopify;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify': typeof Shopify;
  }
}
